// ticket.schema.json source — ticket frontmatter shape (spec §5.1 "Định
// nghĩa ticket được version bằng git"). Matches team-ai-pack
// templates/autopilot/ticket.tpl.md frontmatter keys exactly (pytest
// test_ticket_template_frontmatter_parses_with_stdlib_yaml_parser checks the
// same key set on the pack side, independently).
import { z } from "zod";

export const TicketKindSchema = z.enum([
  "survey",
  "plan",
  "testcase",
  "implement",
  "test",
  "fix",
  "review",
  "decision",
  "revert",
  "deploy",
]);

/** Bug I (P15): the feature-dispatch gate guard discriminates on ticket kind, so it needs the union as a type, not just the schema. */
export type TicketKind = z.infer<typeof TicketKindSchema>;

export const TicketExecutorSchema = z.enum(["claude", "controller"]);

/**
 * C1: one checkable condition for "this ticket delivered what it promised".
 *
 * `evidence` is optional at the SCHEMA layer but not at the verifier layer:
 * result-verifier-checks.ts rejects an attempt that ticks `done: true` with
 * nothing to point at. The split is deliberate and matches the argument
 * already recorded for `evidence[]` itself — a ticket is authored BEFORE the
 * work runs (every row starts `done: false`, evidence unknown), so requiring
 * it in the schema would make an honest freshly-written ticket unparseable.
 */
export const TicketAcceptanceSchema = z
  .object({
    /** The condition, in the same business language as `outcome`. */
    text: z.string().min(1),
    done: z.boolean(),
    /** Where the claim leans — report path, recipe id, commit. */
    evidence: z.string().min(1).optional(),
  })
  .strict();

export type TicketAcceptance = z.infer<typeof TicketAcceptanceSchema>;

export const TicketRuntimeStatusSchema = z.enum([
  "todo",
  "in_progress",
  "done",
  "failed",
  "blocked",
  "cancelled",
  "superseded",
]);

export const TicketSchema = z
  .object({
    id: z.string().regex(/^T-\d{4,}$/, "ticket id phải dạng T-0007"),
    revision: z.number().int().positive(),
    title: z.string().min(1),
    kind: TicketKindSchema,
    executor: TicketExecutorSchema,
    stage: z.string().min(1),
    slice_id: z.string().min(1),
    depends_on: z.array(z.string()),
    agent: z.string().optional(),
    command_adapter: z.string().min(1),
    plan_path: z.string().min(1),
    phase_path: z.string().min(1),
    allowed_paths: z.array(z.string()),
    verification_recipe: z.string().min(1),
    required_capabilities: z.array(z.string()),
    sources: z.array(z.string()),
    // Trace back to the ticket a split came from. Optional: absent on every
    // ticket that was never produced by a split. Kept in the published schema
    // although the automatic split is gone (SOL-06: a person splits now, and
    // may record the parent here).
    split_from: z
      .string()
      .regex(/^T-\d{4,}$/, "split_from phải dạng T-0007")
      .optional(),
    // C1 (spec §C1): what the ticket is FOR, as opposed to how it runs. All
    // three are optional — `.strict()` plus six e2e fixtures and two init
    // seeds carrying the pre-C1 key set means a required field here is a
    // parse error on every ticket that already exists.
    /** The delivered behavior in business language — no file paths, no adapter names. */
    outcome: z.string().min(1).optional(),
    /** Checkable conditions; the verifier rejects `done: true` with no `evidence`. */
    acceptance: z.array(TicketAcceptanceSchema).optional(),
    /** What this ticket deliberately does NOT cover (same voice as the result's `uncovered_areas`). */
    coverage_limits: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type Ticket = z.infer<typeof TicketSchema>;

// Shape of `proposed_changes[].content` for `op: "create"` — a ticket WITHOUT
// `id` (the id comes from the `target` file name, never from the worker: a
// worker choosing its own id is the Bug K shape). Published as
// `templates/autopilot/proposed-change-content.schema.json` (schemas/index.ts)
// and enforced by supervisor-proposal-application.ts from this SAME export.
// Dogfood 2026-09-15 (mission ME-2026-42f25f): a real survey worker read the
// published ticket.schema.json (which REQUIRES `id`), sent all 15 keys, and
// was rejected with `Unrecognized key: "id"` — "schema is the truth" was false
// for exactly this contract until it had its own schema.
export const ProposedChangeContentSchema = TicketSchema.omit({ id: true });
export type ProposedChangeContent = z.infer<typeof ProposedChangeContentSchema>;
